import { ApprovalProcess, CostCenter, Employee, Expense, RightEnum } from "@finway-group/shared/lib/models"
import { getApproverIdsOfApprovalProcesses } from "@finway-group/shared/lib/utils/approvalProcess.utils"
import { Select } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"
import { useDispatch } from "react-redux"
import { Link } from "react-router-dom"
import { patchExpense } from "~Shared/store/actions/expense/expenseActions"
import DeletedTooltip from "../../../../../components/deletedTooltip"
import { useCompany } from "../../../../../shared/hooks/company.hooks"
import { useSubCostCenters } from "../../../../../shared/hooks/costCenter.hooks"
import { useLoggedInEmployeeProfile } from "../../../../../shared/hooks/user.hooks"
import { ApprovalProcessService, AuthzService, NotificationService } from "../../../../../shared/services"
import { getCostCenterLabel } from "../../../../../shared/utils/costCenter.utils"
import { isSplitPropertiesSame } from "../../../../../shared/utils/expense.utils"
import { doFilterSelect } from "../../../../../shared/utils/helper.utils"
import useStateIfMounted from "../../../../../shared/utils/hooks/useStateIfMounted"
import { getPopupAnchor } from "../../../../../shared/utils/popup.utils"

interface CostCenterFieldProps {
    expense: Expense
    setIsExpenseUpdating?: React.Dispatch<React.SetStateAction<boolean>>
    setSelectedExpense?: (expense: Expense | undefined) => void
    costCenter?: CostCenter
    requestedBy: Employee
    isReadOnly: boolean
    areInputsDisabled: boolean
}

export const CostCenterField = ({ expense, requestedBy, costCenter, setIsExpenseUpdating, isReadOnly, areInputsDisabled, setSelectedExpense }: CostCenterFieldProps) => {
    const { t } = useTranslation()
    const dispatch = useDispatch()

    const loggedInUser = useLoggedInEmployeeProfile()
    const isRequester = loggedInUser.id === expense.requestedBy._id
    const isApproverOfAnyApprovalProcess = getApproverIdsOfApprovalProcesses(expense.approvalProcesses).includes(loggedInUser.id)
    const company = useCompany()
    const { showCostCentersIds } = company

    const isTeamApprover = AuthzService.isRightGrantedForLoggedInUser(RightEnum.EXPENSE__TEAM__APPROVE) && loggedInUser.activeCompanyProfile.team === requestedBy.team
    const costCenters = useSubCostCenters(true)
    const isUniform = isSplitPropertiesSame(expense.splits)
    const showCostCenterSelect = expense.splits.every((s) => !s.costCenter) && !expense.costCenter
    const isCostCenterApprover = AuthzService.isRightGrantedForLoggedInUser(RightEnum.CC1__ALL__OWNER) && loggedInUser.id === costCenter?.responsibleUser

    const canReadCostCenter = AuthzService.isRightGrantedForLoggedInUser(RightEnum.CC1__ALL__READ) || isCostCenterApprover

    const [isCostCenterUpdating, setIsCostCenterUpdating] = useStateIfMounted(false)

    const onCostCenterUpdate = async (value: string) => {
        try {
            setIsCostCenterUpdating(true)
            setIsExpenseUpdating?.(true)
            const payload: { costCenter: string; approvalProcesses?: Array<ApprovalProcess> } = { costCenter: value }

            // Rebuild approval process with cost center change. Expenses inside folder do not change its aproval processes and follow the folder approval processes.
            if (!expense.folderId) {
                const approvalProcesses = await ApprovalProcessService.getApprovalProcessesAfterExpenseUpdate(new Expense({ ...expense, costCenter: value }), company, {
                    valuesChange: { vendor: { previousValue: expense.costCenter, currentValue: value } },
                    awaitConfirmation: true,
                })
                if (!approvalProcesses) {
                    return
                }
                payload.approvalProcesses = approvalProcesses
            }

            const updatedExpenseResponse = await patchExpense(expense.id, payload)(dispatch)
            setSelectedExpense?.(updatedExpenseResponse)
        } catch (err) {
            NotificationService.showErrorNotificationBasedOnResponseError(err, t("error:error"))
        } finally {
            setIsCostCenterUpdating(false)
            setIsExpenseUpdating?.(false)
        }
    }

    return (
        <li>
            <span className="title">{t("label:cost_center")}</span>
            {(canReadCostCenter || isRequester || isApproverOfAnyApprovalProcess || isTeamApprover) && costCenter && isUniform.costCenter ? (
                <div className="flex gap-10">
                    {canReadCostCenter ? (
                        <Link to={`/costCenters/${costCenter._id}`}>
                            <span className="block truncate max-w-180 text-right">{costCenter.name}</span>
                        </Link>
                    ) : (
                        <span className="block truncate max-w-180 text-right">{costCenter.name}</span>
                    )}
                    {costCenter?.deleted && <DeletedTooltip className="mt-4" translationKey="tooltips:cost_center.was_deleted" deletionDate={costCenter?.dateDeleted} />}
                </div>
            ) : showCostCenterSelect ? (
                <Select
                    onSelect={onCostCenterUpdate}
                    className="w-1/2"
                    showSearch
                    filterOption={doFilterSelect}
                    getPopupContainer={getPopupAnchor()}
                    defaultValue=""
                    loading={isCostCenterUpdating}
                    disabled={isCostCenterUpdating || isReadOnly || areInputsDisabled}
                >
                    <Select.Option value="" disabled>
                        <div className="flex items-center overflow-hidden">
                            <span>-</span>
                        </div>
                    </Select.Option>
                    {costCenters.map((cc, index) => (
                        <Select.Option key={index} value={cc._id} id={`costcenter-option-${index}`} label={getCostCenterLabel(cc, showCostCentersIds)}>
                            {getCostCenterLabel(cc, showCostCentersIds)}
                        </Select.Option>
                    ))}
                </Select>
            ) : (
                <span className="text truncate">{isUniform.costCenter && costCenter ? costCenter?.name : expense.splits.length > 1 ? t("label:splitted") : "-"}</span>
            )}
        </li>
    )
}
